import moment from 'moment';
import type {
    AdvisoryNeedTaskCreateDto,
    AdvisoryNeedTaskDto,
    AdvisoryNeedTaskUpdateDto,
    EnrichedClientDto,
    LifeEventCreateDto,
    LifeEventDto,
    LifeEventUpdateDto,
    MaturityDto,
    MaturityEventUpdateDto,
    TalkingPointDto,
    TopicDto,
} from '../api/generated/client';
import type { AdvisoryNeedDto } from '../interfaces/advisory-need';
import type { AdvisoryOrLifeEventType, RoadmapEvent } from '../interfaces/event';

export const buildCreateAdvisoryNeedTask = (task: AdvisoryNeedTaskDto | AdvisoryNeedTaskCreateDto): AdvisoryNeedTaskCreateDto => ({
    dueDate: task.dueDate ?? '',
    reminderDate: task.reminderDate,
    clientId: task.clientId!,
    topicId: task.topicId!,
    advisoryNeedId: task.advisoryNeedId!,
    talkingPointId: task.talkingPointId,
    note: task.note,
    name: task.name,
    isCashFlowRelevant: task.isCashFlowRelevant,
    type: task.type,
    status: task.status!,
    currency: task.currency,
    amount: task.amount,
});
export const buildUpdateAdvisoryNeedTask = (task: AdvisoryNeedTaskDto | AdvisoryNeedTaskCreateDto): AdvisoryNeedTaskUpdateDto => ({
    dueDate: task.dueDate ?? '',
    reminderDate: task.reminderDate,
    note: task.note,
    name: task.name,
    isCashFlowRelevant: task.isCashFlowRelevant,
    type: task.type,
    status: task.status!,
    currency: task.currency,
    amount: task.amount,
});

export const buildAdvisoryNeedTask = (
    language: string,
    topic?: TopicDto,
    talkingPoint?: TalkingPointDto,
    advisoryNeed?: AdvisoryNeedDto,
    client?: EnrichedClientDto,
): AdvisoryNeedTaskDto => ({
    enforceDisable: undefined,
    note: undefined,
    amount: undefined,
    signedAmount: undefined,
    topicName: topic?.name,
    id: -1,
    name: advisoryNeed!.name[language],
    currency: client?.currency,
    talkingPointId: talkingPoint!.id,
    advisoryNeedId: advisoryNeed?.id,
    clientId: client?.id,
    topicId: topic?.id,
    reminderDate: '',
    dueDate: '',
    isCashFlowRelevant: false,
    advisoryNeedName: undefined,
    type: undefined,
    createdAt: undefined,
    eventType: 'ADVISORY_NEED_TASK',
    status: 'DEFINED',
    notification: 'NONE',
    topicKey: topic?.key,
});
export const buildCreateLifeEvent = (event: LifeEventDto): LifeEventCreateDto => ({
    name: event.name!,
    isCashFlowRelevant: event.isCashFlowRelevant!,
    type: event.type!,
    status: event.status!,
    currency: event.currency,
    amount: event.amount,
    dueDate: event.dueDate!,
    reminderDate: event.reminderDate,
    clientId: event.clientId!,
    topicId: event.topicId!,
    note: event.note,
});
export const buildUpdateLifeEvent = (event: LifeEventDto): LifeEventUpdateDto => ({
    name: event.name!,
    isCashFlowRelevant: event.isCashFlowRelevant!,
    type: event.type,
    status: event.status!,
    currency: event.currency,
    amount: event.amount,
    dueDate: event.dueDate!,
    reminderDate: event.reminderDate,
    note: event.note,
});

export const buildUpdateMaturityEvent = (event: MaturityEventUpdateDto): MaturityEventUpdateDto => ({
    qualifiedId: event.qualifiedId,
    qualifiedKey: event.qualifiedKey,
    status: event.status,
    refinancing: event.refinancing,
});

export const isMaturity = (event?: RoadmapEvent): event is MaturityDto => {
    return event ? 'qualifiedKey' in (event as MaturityDto) && (event as MaturityDto).eventType === 'MATURITY' : false;
};

export const isRefinanced = (event?: RoadmapEvent): boolean => isMaturity(event) && event.refinancing;

export const getRoadmapEntryDisplayName = (event: RoadmapEvent | undefined, refinancedLabel: string): string =>
    event ? `${event.name ?? ''}${isRefinanced(event) ? ` ${refinancedLabel}` : ''}` : '';

export const isLifeEvent = (event?: RoadmapEvent): event is LifeEventDto => {
    return event ? 'id' in (event as LifeEventDto) && (event as LifeEventDto).eventType === 'LIFE_EVENT' : false;
};

export const isAdvisoryNeedTask = (event?: RoadmapEvent): event is AdvisoryNeedTaskDto => {
    return event ? 'id' in event && event.eventType === 'ADVISORY_NEED_TASK' && 'advisoryNeedId' in event && 'talkingPointId' in event : false;
};

export const isAdvisoryEventNew = (event?: AdvisoryOrLifeEventType): event is AdvisoryNeedTaskDto => {
    return event ? 'id' in event && event.eventType === 'ADVISORY_NEED_TASK' && 'advisoryNeedId' in event && 'talkingPointId' in event : false;
};

export const closestEventAfterDate = (events: RoadmapEvent[], currentDate: moment.Moment): RoadmapEvent | undefined => {
    return (
        events.filter((event) => moment(event.dueDate).isSameOrAfter(currentDate, 'day')).sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate)))[0] ||
        undefined
    );
};

export const isRoadmapEventUpcoming = (event: RoadmapEvent): boolean => {
    if (!event.dueDate) return false;

    const due = moment(event.dueDate);

    return due.isValid() && !due.isBefore(moment().startOf('day'));
};
